import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { Me } from "@jaks/contract";
import { api } from "@jaks/core/api-client";
import { MODULES } from "../manifest.ts";
import { Icon } from "./icons.tsx";
import { Bell } from "./bell.tsx";
import logoUrl from "../assets/wordmark-white.png";

const GROUPS = ["Today", "Work", "Sell", "Buy", "Company"] as const;

export function useMe() {
  return useQuery<Me>({ queryKey: ["me"], queryFn: () => api<Me>("/api/me"), staleTime: 5 * 60_000, retry: false });
}

export function Shell() {
  const me = useMe();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const allowedIds = (me.data as { allowedModules?: string[] } | undefined)?.allowedModules;
  const visible = MODULES.filter((m) =>
    (allowedIds ? allowedIds.includes(m.id) : true) && (!m.requires || m.requires.some((r) => me.data?.roles.includes(r))),
  );
  const activeId = visible.find((m) => path === `/${m.id}` || path.startsWith(`/${m.id}/`))?.id ?? (path === "/" ? "home" : "");
  const tabs = visible.filter((m) => m.mobileTab).slice(0, 4);

  if (me.isLoading) return <div style={{ display: "grid", placeItems: "center", height: "100%" }} className="muted">Signing you in…</div>;

  return (
    <div className="shell">
      <aside className="sidebar">
        <img src={logoUrl} alt="JAKS" className="logo" width={150} />
        <nav aria-label="Modules">
          {GROUPS.map((g) => {
            const items = visible.filter((m) => m.group === g).sort((a, b) => a.order - b.order);
            if (!items.length) return null;
            return (
              <div key={g} className="nav-group">
                <div className="g">{g}</div>
                {items.map((m) => (
                  <Link key={m.id} to={`/${m.id}`} className={"nav-a" + (m.id === activeId ? " active" : "")}>
                    <Icon name={m.icon} /> <span>{m.label}</span>
                  </Link>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="util">
          <QuickLinks />
          <div className="me-row">
            <div className="avatar">{initials(me.data?.name ?? "?")}</div>
            <div className="who"><b>{me.data?.name}</b><span>{me.data?.email}</span></div>
          </div>
          <div className="me-actions">
            <Link to="/settings" className="me-btn"><Icon name="gear" size={15} /> Settings</Link>
            <button className="me-btn" onClick={signOut}><Icon name="signout" size={15} /> Sign out</button>
          </div>
        </div>
      </aside>
      <main className="main"><Outlet /></main>
      <Bell variant="mobile" />
      <nav className="tabbar" aria-label="Primary">
        {tabs.map((m) => (
          <Link key={m.id} to={`/${m.id}`} className={m.id === activeId ? "active" : ""}>
            <Icon name={m.icon} size={22} /> <span>{m.label}</span>
          </Link>
        ))}
        <Link to="/more" className={activeId === "more" ? "active" : ""}><Icon name="more" size={22} /> <span>More</span></Link>
      </nav>
    </div>
  );
}
const LINK_ICONS: Record<string, string> = {
  JobTread: "briefcase", Gmail: "mail", Calendar: "calendarDay", Drive: "driveTri", Asana: "asana",
};

/** The tool row from the old portal: icons with tooltips, bell and theme at the end. */
function QuickLinks() {
  const q = useQuery({ queryKey: ["quick-links"], queryFn: () => api<{ links: Array<{ label: string; url: string }> }>("/api/home/quick-links"), staleTime: 10 * 60_000 });
  const links = q.data?.links ?? [];
  return (
    <div className="quick-links" aria-label="Quick links">
      {links.map((l) => (
        <a key={l.url} href={l.url} target="_blank" rel="noreferrer" title={l.label} aria-label={l.label}>
          <Icon name={LINK_ICONS[l.label] ?? "open"} size={16} />
        </a>
      ))}
      <span className="ql-spacer" />
      <Bell variant="sidebar" />
      <button title="Theme" onClick={toggleTheme} aria-label="Toggle theme">◐</button>
    </div>
  );
}

function signOut() {
  fetch("/api/auth/logout", { method: "POST", headers: { "x-amz-content-sha256": "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855" } })
    .finally(() => location.assign("/"));
}

function initials(n: string) { return n.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase(); }
function toggleTheme() {
  const cur = document.documentElement.getAttribute("data-theme");
  const next = cur === "dark" ? "light" : "dark";
  document.documentElement.setAttribute("data-theme", next);
  localStorage.setItem("jaks-theme", next);
}
